import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { landingRoute } from './navigation';
import AdminLayout from './components/AdminLayout';
import AccessDenied from './components/AccessDenied';
import NoScreenAccess from './components/NoScreenAccess';
import RequireScreen from './components/RequireScreen';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import GaragesPage from './pages/GaragesPage';
import GarageDetailPage from './pages/GarageDetailPage';
import CategoriesPage from './pages/CategoriesPage';
import ServiceApprovalsPage from './pages/ServiceApprovalsPage';
import GarageServiceDetailPage from './pages/GarageServiceDetailPage';
import TranslationsPage from './pages/TranslationsPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import SettlementsPage from './pages/SettlementsPage';
import SettlementDetailPage from './pages/SettlementDetailPage';
import FinancePage from './pages/FinancePage';
import LookupsPage from './pages/LookupsPage';
import TermsDocumentsPage from './pages/TermsDocumentsPage';
import UserDetailPage from './pages/UserDetailPage';
import GarageChangeRequestsPage from './pages/GarageChangeRequestsPage';
import GarageChangeRequestDetailPage from './pages/GarageChangeRequestDetailPage';
import AdminRolesPage from './pages/AdminRolesPage';
import PickupTimeSlotsPage from './pages/PickupTimeSlotsPage';
import DynamicConfigPage from './pages/DynamicConfigPage';
import OfferingPendingVariantsPage from './pages/OfferingPendingVariantsPage';
import GarageServicesWorkspacePage from './pages/GarageServicesWorkspacePage';
import ReportedReviewsPage from './pages/ReportedReviewsPage';
import PromotionsPage from './pages/PromotionsPage';
import PromotionDetailPage from './pages/PromotionDetailPage';
import OfferReviewPage from './pages/OfferReviewPage';
import OfferEditPage from './pages/OfferEditPage';
import PackageReviewPage from './pages/PackageReviewPage';
import PackageEditPage from './pages/PackageEditPage';
import ServiceProposalsPage from './pages/ServiceProposalsPage';
import AuditPage from './pages/AuditPage';
import MarketingPage from './pages/MarketingPage';
import DealersPage from './pages/DealersPage';
import DealerDetailPage from './pages/DealerDetailPage';
import DealerPacksPage from './pages/DealerPacksPage';
import { useFeatureFlags } from './context/FeatureFlagsContext';

function App() {
  const auth = useAuth();
  const features = useFeatureFlags();

  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (auth.isAccessDenied) {
    return <AccessDenied onLogout={auth.logout} />;
  }

  // Spec 050 — "/" is no longer a fixed hop to /dashboard: dashboard is itself a
  // grantable screen, so an admin without it would land on a redirect they
  // cannot open.
  const home = landingRoute(auth.isSuperAdmin, auth.hasScreen, features);

  return (
    <Routes>
      <Route path="/login" element={auth.isAuthenticated ? <Navigate to={home ?? '/'} replace /> : <LoginPage auth={auth} />} />
      <Route element={auth.isAuthenticated ? <AdminLayout auth={auth} /> : <Navigate to="/login" replace />}>
        <Route path="/" element={home ? <Navigate to={home} replace /> : <NoScreenAccess />} />

        <Route path="/dashboard" element={<RequireScreen screen="dashboard"><DashboardPage /></RequireScreen>} />

        <Route path="/users" element={<RequireScreen screen="users"><UsersPage /></RequireScreen>} />
        <Route path="/users/:id" element={<RequireScreen screen="users"><UserDetailPage /></RequireScreen>} />

        <Route path="/garages" element={<RequireScreen screen="garages"><GaragesPage /></RequireScreen>} />
        <Route path="/garages/:id" element={<RequireScreen screen="garages"><GarageDetailPage /></RequireScreen>} />

        <Route path="/categories" element={<RequireScreen screen="service-catalog"><CategoriesPage /></RequireScreen>} />

        <Route path="/service-approvals" element={<RequireScreen screen="service-approvals"><ServiceApprovalsPage /></RequireScreen>} />
        {/* Spec 062 — garage offer review (Service approvals → Offers tab). */}
        <Route path="/service-approvals/offers/:id" element={<RequireScreen screen="service-approvals"><OfferReviewPage /></RequireScreen>} />
        <Route path="/service-approvals/offers/:id/edit" element={<RequireScreen screen="service-approvals"><OfferEditPage /></RequireScreen>} />
        {/* Spec 081b — partner package review (Service approvals → Packages tab). */}
        <Route path="/service-approvals/packages/:id" element={<RequireScreen screen="service-approvals"><PackageReviewPage /></RequireScreen>} />
        <Route path="/service-approvals/packages/:id/edit" element={<RequireScreen screen="service-approvals"><PackageEditPage /></RequireScreen>} />
        <Route path="/service-approvals/:garageId" element={<RequireScreen screen="service-approvals"><GarageServiceDetailPage /></RequireScreen>} />
        <Route path="/offerings/:offeringId/pending-variants" element={<RequireScreen screen="service-approvals"><OfferingPendingVariantsPage /></RequireScreen>} />
        <Route path="/garages/:garageId/services" element={<RequireScreen screen="service-approvals"><GarageServicesWorkspacePage /></RequireScreen>} />
        {/* Spec 069 — the proposals page existed without a route; the dashboard tile links here. */}
        <Route path="/service-proposals" element={<RequireScreen screen="service-approvals"><ServiceProposalsPage /></RequireScreen>} />

        <Route path="/translations" element={<RequireScreen screen="translations"><TranslationsPage /></RequireScreen>} />

        <Route path="/orders" element={<RequireScreen screen="orders"><OrdersPage /></RequireScreen>} />
        <Route path="/orders/:orderId" element={<RequireScreen screen="orders"><OrderDetailPage /></RequireScreen>} />

        <Route path="/settlements" element={<RequireScreen screen="settlements"><SettlementsPage /></RequireScreen>} />
        <Route path="/settlements/:settlementId" element={<RequireScreen screen="settlements"><SettlementDetailPage /></RequireScreen>} />

        <Route path="/lookups" element={<RequireScreen screen="lookups"><LookupsPage /></RequireScreen>} />
        <Route path="/terms-documents" element={<RequireScreen screen="terms-documents"><TermsDocumentsPage /></RequireScreen>} />
        {/* #1113 — Finance opens with either screen; the page shows only the tabs the admin holds. */}
        <Route path="/finance" element={<RequireScreen screen={['finance', 'finance-config']}><FinancePage /></RequireScreen>} />

        {/* Spec 059 — promo codes; finance admins read promo spend on Finance → Overview (DV-1). */}
        <Route path="/promotions" element={<RequireScreen screen="promotions"><PromotionsPage /></RequireScreen>} />
        <Route path="/promotions/:id" element={<RequireScreen screen="promotions"><PromotionDetailPage /></RequireScreen>} />

        {/* Spec 085 — dealer credit: dealers, their users, packs, requests, mark-paid, documents. */}
        <Route path="/dealers" element={<RequireScreen screen="dealers"><DealersPage /></RequireScreen>} />
        <Route path="/dealers/packs" element={<RequireScreen screen="dealers"><DealerPacksPage /></RequireScreen>} />
        <Route path="/dealers/:id" element={<RequireScreen screen="dealers"><DealerDetailPage /></RequireScreen>} />

        <Route path="/change-requests" element={<RequireScreen screen="change-requests"><GarageChangeRequestsPage /></RequireScreen>} />
        <Route path="/change-requests/:id" element={<RequireScreen screen="change-requests"><GarageChangeRequestDetailPage /></RequireScreen>} />

        <Route path="/pickup-time-slots" element={<RequireScreen screen="pickup-time-slots"><PickupTimeSlotsPage /></RequireScreen>} />
        <Route path="/dynamic-config" element={<RequireScreen screen="dynamic-config"><DynamicConfigPage /></RequireScreen>} />
        <Route path="/reported-reviews" element={<RequireScreen screen="reported-reviews"><ReportedReviewsPage /></RequireScreen>} />

        {/* Spec 078 — store taps, page loads and tap rate. */}
        <Route path="/marketing" element={<RequireScreen screen="marketing"><MarketingPage /></RequireScreen>} />

        {/* Spec 069 — unified admin audit; absent while garagat.admin-audit.screen-enabled is off. */}
        <Route
          path="/audit"
          element={features.auditScreen
            ? <RequireScreen screen="audit"><AuditPage /></RequireScreen>
            : (features.loaded ? <Navigate to={home ?? '/'} replace /> : <div className="admin-loading"><div className="admin-loading-spinner"></div></div>)}
        />

        {/* Super-admin-only; the page guards itself and the service enforces it. */}
        <Route path="/admin/roles" element={<AdminRolesPage />} />
      </Route>
    </Routes>
  );
}

export default App;
